import {
  ApolloClient,
  ApolloError,
  DocumentNode,
  FetchResult,
  OperationVariables,
  TypedDocumentNode,
  useMutation,
} from '@apollo/client';
import { CAPTCHA_TOKEN_HEADER, TurnstileWidgetProps, useCaptchaChallenge } from '@kompare/captcha-react';
import { useRef, useState } from 'react';
import { captchaStatusOf, submitStatusOf } from 'src/common/api/apollo/captcha';
import {
  CaptchaPage,
  describeInput,
  i18n,
  newChallengeId,
  reportCaptchaRequired,
  reportSubmitOutcome,
  reportTurnstileEvent,
} from 'src/common/services';
import config from 'src/config';

export interface SubmitHandlers<TData> {
  onSuccess: (result: FetchResult<TData>) => void;
  /** Every failure except CAPTCHA_REQUIRED — the hook owns that branch. */
  onError?: (error: ApolloError) => void;
  /** Runs after EVERY attempt, including one that ended in a challenge (drop loaders here). */
  onSettled?: () => void;
}

export interface CaptchaState {
  /** Render the widget when true (and keep the submit button disabled — the widget resubmits). */
  required: boolean;
  /** Copy to show under the widget while a challenge is due; null otherwise. */
  message: string | null;
  widgetProps: Pick<TurnstileWidgetProps, 'siteKey' | 'onToken' | 'onEvent' | 'resetSignal'>;
}

interface Options {
  page: CaptchaPage;
  /** The additional funnel's client; defaults to the ApolloProvider's. */
  client?: ApolloClient<object>;
}

/**
 * `createSo` behind health-api's captcha ladder (`@kompare/rate-limit` README,
 * "Conditional captcha"). On `CAPTCHA_REQUIRED` (HTTP 200, `extensions.statusCode` 428)
 * the hook arms the Turnstile widget, keeps the denied `variables` object and resends
 * that same object with the token in the `x-captcha-token` header; the caller's
 * handlers see the retry's outcome as if it were the first attempt. A 428 on the retry
 * resets the widget for another solve; any other failure of the retry ends the
 * challenge and re-enables the button, so the next attempt is an explicit one.
 */
export const useCreateSoWithCaptcha = <TData = any, TVars extends OperationVariables = OperationVariables>(
  document: DocumentNode | TypedDocumentNode<TData, TVars>,
  { page, client }: Options
) => {
  // errorPolicy 'none' is required: under 'all' a CAPTCHA_REQUIRED response resolves
  // instead of throwing and the challenge branch below never runs.
  const [mutate, { loading }] = useMutation<TData, TVars>(document, { client, errorPolicy: 'none' });
  const challenge = useCaptchaChallenge();
  const pendingRef = useRef<{ variables: TVars; handlers: SubmitHandlers<TData> } | null>(null);
  const inFlightRef = useRef(false);
  const [message, setMessage] = useState<string | null>(null);
  // `useCaptchaChallenge` stays `required` once armed; this hides the widget after a
  // terminal error so a fresh token does not keep replaying a failing request.
  const [dismissed, setDismissed] = useState(false);
  // Telemetry correlation id shared by every event of the challenge in progress.
  const challengeIdRef = useRef<string | null>(null);

  const run = async (variables: TVars, handlers: SubmitHandlers<TData>, token?: string) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const input = describeInput((variables as { input?: { phoneNumber?: string } })?.input?.phoneNumber);
    try {
      const result = await mutate({
        variables,
        context: token ? { headers: { [CAPTCHA_TOKEN_HEADER]: token } } : undefined,
      });
      reportSubmitOutcome(page, 200, input, !!token, challengeIdRef.current ?? undefined);
      challengeIdRef.current = null;
      pendingRef.current = null;
      setMessage(null);
      handlers.onSuccess(result);
    } catch (e) {
      const error = e as ApolloError;
      const challenged = challenge.handleStatus(captchaStatusOf(error));
      if (challenged && !challengeIdRef.current) challengeIdRef.current = newChallengeId();
      reportSubmitOutcome(page, submitStatusOf(error), input, !!token, challengeIdRef.current ?? undefined);
      if (challenged) {
        // Keep the exact payload for the token retry; `handleStatus` resets the widget on a repeat.
        pendingRef.current = { variables, handlers };
        setDismissed(false);
        reportCaptchaRequired(page, challengeIdRef.current as string, input);
        setMessage(i18n.t('common.captchaRequired'));
        return;
      }
      // Terminal failure: drop the pending retry.
      challengeIdRef.current = null;
      pendingRef.current = null;
      setMessage(null);
      if (token) setDismissed(true);
      handlers.onError?.(error);
    } finally {
      inFlightRef.current = false;
      handlers.onSettled?.();
    }
  };

  const submit = (variables: TVars, handlers: SubmitHandlers<TData>) => {
    run(variables, handlers);
  };

  const onToken = (token: string) => {
    const pending = pendingRef.current;
    if (pending) run(pending.variables, pending.handlers, token);
  };

  const captcha: CaptchaState = {
    required: challenge.required && !dismissed,
    message,
    widgetProps: {
      siteKey: config.REACT_APP_TURNSTILE_SITE_KEY,
      onToken,
      onEvent: (event) => reportTurnstileEvent(page, event, challengeIdRef.current ?? undefined),
      resetSignal: challenge.resetSignal,
    },
  };

  return { submit, loading, captcha };
};
